<template>
  <div class="fade-setting-page">
    <div id="listWidget" class="mb-4">
    <!-- 단계별 안내 -->
    <div class="instruction-card card-item mb-2">
      <div class="instruction-header">
        <i class="fa-solid fa-list-ol me-2 text-primary"></i>
        <span class="instruction-title">페이드 설정</span>
      </div>
      <div class="instruction-body">
        <div class="instruction-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <span>원하는 페이드 전환 속도를 <code>체크박스</code>에 체크해주세요.</span>
          </div>
        </div>
        <div class="instruction-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <span><code>변경 버튼</code>을 눌러서 변경해주세요.</span>
          </div>
        </div>
      </div>
    </div>

    <div class="fade-list-card card-item mb-3" v-if="FadeList.length > 0">
      <div class="fade-list-card-header">
        <i class="fa-regular fa-hourglass-half me-2 text-primary"></i>
        <span class="fade-list-title">페이드 속도 선택</span>
      </div>
      <div class="fade-list-card-body">
        <div
          class="fade-option-card"
          :class="{ 'fade-option-card--selected': selectedFade == fade.value }"
          v-for="(fade, index) in FadeList"
          :key="fade.value"
          @click="onSelectFadeChange(fade.value, index)"
        >
          <div class="form-check fade-option-check">
            <input
              class="form-check-input fade-option-radio"
              type="radio"
              :name="`fadeOption`"
              :id="`gridCheck${index}`"
              :value="fade.value"
              :checked="selectedFade == fade.value"
              @change="() => onSelectFadeChange(fade.value, index)"
            />
            <label class="form-check-label" :for="`gridCheck${index}`">{{ fade.text }}</label>
          </div>
          <i class="fa-solid fa-check text-primary fade-option-check-icon" v-if="selectedFade == fade.value"></i>
        </div>
      </div>
    </div>
    </div>

    <div id="footer" class="app-footer ms-0">
    <footer class="mt-auto">
      <div class="row">
        <div class="col" />
        <div class="col" />
        <div class="col">
          <button
            type="button"
            class="btn btn-primary btn-lg footer-btn"
            @click="onBatchSettingClick"
          >
            변경
          </button>
        </div>
      </div>
    </footer>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  getCurrentInstance,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  reactive,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import useMqttControl from '@/common/hooks/useMqttControl';
import Axios, { DeviceApi } from '@/api';
import { FadeList } from '@/common/constants';

const store = useStore();
const router = useRouter();
const { emitter, mobile, logger } =
  getCurrentInstance().appContext.config.globalProperties;
const mqttControl = reactive(useMqttControl());
const zone = computed(() => store.getters['home/getCurrentZone']);
const selectedFade = ref(2);

onMounted(() => {
  // LightSettingPage onBeforeUnmount가 헤더를 기본값으로 되돌린 뒤에 적용해야 함
  emitter.emit('onChangeHeaderSetting', {
    title: '페이드 설정',
    isShowMenu: false,
    isShowZone: false,
    isShowProfile: false,
    isShowRightHome: true,
  });

  if (zone.value) {
    selectedFade.value = zone.value.fade === undefined ? selectedFade.value : zone.value.fade;
  }
});

onBeforeUnmount(() => {
  emitter.emit('onChangeHeaderSetting', {
    title: '',
    isShowMenu: true,
    isShowZone: true,
    isShowProfile: true,
    isShowRightHome: false,
  });
  store.dispatch('home/fetchAllZones');
});

const onBatchSettingClick = async () => {
  if (selectedFade.value === undefined) {
    emitter.emit('showPopAlert', {
      description: '페이드 속도를 선택해주세요.',
    });
    return;
  }

  try {
    zone.value?.controllers?.forEach(controller => {
      mqttControl.onPublishController(controller._id, {
        serial: controller._id,
        cmd: 'ds',
        type: 1,
        data: {
          setting: selectedFade.value
        }
      });
    });

    emitter.emit('showPopAlert', {
      description: "페이드 속도가 변경되었습니다.",
      okCallback: () => router.go(-1),
      cancelCallback: () => router.go(-1),
    });
  } catch (e) {
    emitter.emit('showPopAlert', {
      description: "페이드 속도 설정에 실패했습니다.",
    });
  }
};

const onSelectFadeChange = (value, index) => {
  selectedFade.value = value;
};
</script>

<style scoped>
/* instruction-card 스타일 */
.instruction-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.instruction-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.instruction-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.instruction-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.instruction-body {
  padding: 0.75rem 1rem;
}

.instruction-step {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.instruction-step:last-child {
  margin-bottom: 0;
}

.step-number {
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0d6efd;
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 0.75rem;
}

.step-content {
  flex: 1;
  padding-top: 0.1rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #495057;
}

.step-content strong {
  color: #0d6efd;
  font-weight: 600;
}

.fade-list-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-top: 1rem;
}

.fade-list-card-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.fade-list-title {
  margin-right: 0.25rem;
}

.fade-list-card-body {
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.fade-option-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 0.875rem 1rem;
  cursor: pointer;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.fade-option-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.fade-option-card--selected {
  border-color: #0d6efd;
  box-shadow: 0 2px 8px rgba(13, 110, 253, 0.15);
}

.fade-option-radio {
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  pointer-events: none;
  margin: 0;
  width: 0;
  height: 0;
}

.fade-option-check {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  flex: 1;
  cursor: pointer;
  padding-left: 0;
}

.fade-option-check .form-check-label {
  cursor: pointer;
  font-size: 0.85rem;
  color: #212529;
  margin-left: 0.25rem;
}

.fade-option-check-icon {
  flex-shrink: 0;
  font-size: 0.9rem;
}

.footer-btn {
  width: 100%;
  border-radius: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
</style>
